/**
 * useChatV3 — typed-parts chat hook (PR 3 of response-formatting design).
 *
 * Parallel to `useChatV2`. Only used when the `formatting_v2` flag is on
 * (the corresponding backend gate landed in PR 2). Parses Vercel
 * UIMessageStream v1 frames into typed `UIMessagePart`s sourced from
 * `@intuigence/response-parts`. The legacy `useChatV2` hook is left
 * untouched so the rollout/rollback path is a flag flip.
 *
 * Scope of PR 3 (per `docs/response-formatting-design.md`):
 *   - Parses `text-*` and `tool-*` frames into typed parts.
 *   - Other frame types (`data-structured-response`, `data-timeseries`,
 *     `data-citation`, `data-chart`, `reasoning`) are added in their
 *     respective stacked PRs (5b, 6, 6b). Unknown frame types here
 *     fall through with a `[FORMAT_PARSE_FAILED]` console warning.
 */
import { useCallback, useRef, useState } from 'react'
import {
  ChartPart as ChartPartSchema,
  CitationPart as CitationPartSchema,
  StructuredResponsePart as StructuredResponsePartSchema,
  TimeseriesPart as TimeseriesPartSchema,
  WorkflowProgressPart as WorkflowProgressPartSchema,
  PendingSharePart as PendingSharePartSchema,
  type ChartPart,
  type CitationPart,
  type ReasoningPart,
  type StructuredResponsePart,
  type TextPart,
  type TimeseriesPart,
  type ToolCallPart,
  type ToolResultPart,
  type UIMessagePart,
  type WorkflowProgressPart,
} from '@intuigence/response-parts'

import type { MessageSource } from '@/service/api'

import { getAuthToken } from '../utils/auth'
import {
  CHAT_V2_ENDPOINTS,
  INTUIGENCE_PARTS_HEADER,
  INTUIGENCE_PARTS_VERSION,
  VERCEL_AI_STREAM_HEADER,
  VERCEL_AI_STREAM_VERSION,
} from '../constants'

/** Live assistant message under construction during a stream. */
export interface ChatV3AssistantMessage {
  id: string
  parts: UIMessagePart[]
  /** True from the moment the request fires until the [DONE] sentinel
   *  arrives or the stream errors. The renderer can show a typing dot. */
  streaming: boolean
  /** Set on terminal error. UI can render a retry banner. */
  error?: string
  startedAt: string
  completedAt?: string
}

/** Discriminated union of every Vercel UIMessageStream v1 frame this
 *  parser understands. Mirrors the producer in `ui-message-stream.ts`. */
type IncomingFrame =
  | { type: 'start' }
  | { type: 'start-step' }
  | { type: 'text-start'; id: string }
  | { type: 'text-delta'; id: string; delta: string }
  | { type: 'text-end'; id: string }
  // PR 6b — reasoning lifecycle. supersededAtFinish on reasoning-end
  // tells the renderer to collapse preliminaries; finalSummary
  // replaces them with a short blurb.
  | { type: 'reasoning-start'; id: string }
  | { type: 'reasoning-delta'; id: string; delta: string }
  | {
      type: 'reasoning-end'
      id: string
      supersededAtFinish?: boolean
      finalSummary?: string
    }
  | { type: 'tool-input-start'; toolCallId: string; toolName: string }
  | {
      type: 'tool-input-available'
      toolCallId: string
      toolName: string
      input: unknown
    }
  | {
      type: 'tool-output-available'
      toolCallId: string
      output: unknown
      errorText?: string
    }
  // PR 5b — typed `data-*` parts produced by the backend's
  // streamObject path. Validated by the consumer (the reducer) so a
  // malformed payload becomes a single [FORMAT_PARSE_FAILED] log
  // rather than a runtime crash mid-render.
  | { type: 'data-structured-response'; id: string; data: unknown }
  // PR 6 — timeseries answers with stats + manifestGaps; the
  // renderer enforces the FM8 abstain banner from the typed shape.
  | { type: 'data-timeseries'; id: string; data: unknown }
  // PR 8 (gap closure) — citation + chart parts close the design's
  // part vocabulary. Both validate at the wire boundary.
  | { type: 'data-citation'; id: string; data: unknown }
  | { type: 'data-chart'; id: string; data: unknown }
  | { type: 'data-workflow-progress'; id?: string; data: unknown }
  | { type: 'data-pending-share'; id?: string; data: unknown }
  // Non-standard snapshot frame carrying the accumulated, deduped
  // citation list (`KgSourceCitation[]`, already the snake_case
  // `MessageSource` shape). The kg-agent emits it at every tool
  // boundary + finish; last frame wins. It is NOT a typed
  // `UIMessagePart` — sources are message-level data (mirroring the
  // Python SSE envelope and persisted `Message.sources`), surfaced via
  // `extractSourcesFromFrames` rather than `reduceFramesToParts`.
  | { type: 'data-kg-sources'; sources?: unknown }
  | { type: 'finish-step' }
  | { type: 'finish' }
  | { type: 'error'; errorText: string }

/**
 * Pull the latest citation snapshot out of a frame sequence. The
 * `data-kg-sources` frame is a last-wins snapshot, so we scan forward
 * and keep the most recent frame whose `sources` is an array — a
 * trailing malformed frame therefore can't wipe a valid earlier
 * snapshot. Returns `[]` when no valid snapshot was seen. Pure;
 * exported for unit testing and reuse by `useChatV2`.
 */
export function extractSourcesFromFrames(frames: IncomingFrame[]): MessageSource[] {
  let latest: MessageSource[] = []
  for (const frame of frames) {
    if (frame.type === 'data-kg-sources' && Array.isArray(frame.sources)) {
      latest = frame.sources as MessageSource[]
    }
  }
  return latest
}

interface RunningTextPart {
  kind: 'text'
  id: string
  text: string
}

interface RunningReasoningPart {
  kind: 'reasoning'
  id: string
  text: string
}

interface RunningToolCall {
  kind: 'tool'
  toolCallId: string
  toolName: string
  input?: unknown
}

/**
 * Reduce a sequence of incoming frames into the canonical part array.
 * Pure function — exported for unit testing.
 *
 * Two state buckets handle in-flight parts:
 *   - `runningText`: open `text` parts keyed by id; deltas append.
 *   - `runningTools`: open `tool-call` parts keyed by toolCallId.
 * On terminal frames (`text-end`, `tool-output-available`) the part
 * is committed to `parts` in arrival order. This preserves the model's
 * natural interleave (text → tool call → tool result → more text).
 */
export function reduceFramesToParts(frames: IncomingFrame[]): UIMessagePart[] {
  const parts: UIMessagePart[] = []
  const runningText = new Map<string, RunningTextPart>()
  const runningReasoning = new Map<string, RunningReasoningPart>()
  const runningTools = new Map<string, RunningToolCall>()

  for (const frame of frames) {
    switch (frame.type) {
      case 'text-start':
        runningText.set(frame.id, { kind: 'text', id: frame.id, text: '' })
        break
      case 'text-delta': {
        const open = runningText.get(frame.id)
        if (open) open.text += frame.delta
        break
      }
      case 'text-end': {
        const open = runningText.get(frame.id)
        if (open && open.text.length > 0) {
          const part: TextPart = { type: 'text', text: open.text }
          parts.push(part)
        }
        runningText.delete(frame.id)
        break
      }
      case 'reasoning-start':
        runningReasoning.set(frame.id, {
          kind: 'reasoning',
          id: frame.id,
          text: '',
        })
        break
      case 'reasoning-delta': {
        const open = runningReasoning.get(frame.id)
        if (open) open.text += frame.delta
        break
      }
      case 'reasoning-end': {
        const open = runningReasoning.get(frame.id)
        if (open && open.text.length > 0) {
          const part: ReasoningPart = { type: 'reasoning', text: open.text }
          if (frame.supersededAtFinish !== undefined) {
            part.supersededAtFinish = frame.supersededAtFinish
          }
          if (frame.finalSummary !== undefined) {
            part.finalSummary = frame.finalSummary
          }
          parts.push(part)
        }
        runningReasoning.delete(frame.id)
        break
      }
      case 'tool-input-start':
        runningTools.set(frame.toolCallId, {
          kind: 'tool',
          toolCallId: frame.toolCallId,
          toolName: frame.toolName,
        })
        break
      case 'tool-input-available': {
        const open = runningTools.get(frame.toolCallId)
        if (open) {
          open.input = frame.input
          // Commit the tool-call part immediately on input-available
          // so the renderer can show the call before the result lands.
          const part: ToolCallPart = {
            type: 'tool-call',
            toolCallId: open.toolCallId,
            toolName: open.toolName,
            input: open.input,
          }
          parts.push(part)
        }
        break
      }
      case 'tool-output-available': {
        const open = runningTools.get(frame.toolCallId)
        if (!open) break
        const part: ToolResultPart = {
          type: 'tool-result',
          toolCallId: open.toolCallId,
          toolName: open.toolName,
          output: frame.output,
        }
        if (frame.errorText !== undefined) part.isError = true
        parts.push(part)
        runningTools.delete(frame.toolCallId)
        break
      }
      case 'data-structured-response': {
        // Validate at the wire boundary — a malformed payload is
        // dropped with a single warning rather than rendering as
        // junk. Per design §"Schema contract tests", the contract is
        // the @intuigence/response-parts schema and the backend
        // already Zod-parsed before emit.
        const result = StructuredResponsePartSchema.safeParse({
          type: 'data-structured-response',
          ...((frame.data as Record<string, unknown>) ?? {}),
        })
        if (result.success) {
          const part: StructuredResponsePart = result.data
          parts.push(part)
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FORMAT_PARSE_FAILED] data-structured-response', result.error)
        }
        break
      }
      case 'data-timeseries': {
        const result = TimeseriesPartSchema.safeParse({
          type: 'data-timeseries',
          ...((frame.data as Record<string, unknown>) ?? {}),
        })
        if (result.success) {
          const part: TimeseriesPart = result.data
          parts.push(part)
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FORMAT_PARSE_FAILED] data-timeseries', result.error)
        }
        break
      }
      case 'data-citation': {
        const result = CitationPartSchema.safeParse({
          type: 'data-citation',
          ...((frame.data as Record<string, unknown>) ?? {}),
        })
        if (result.success) {
          const part: CitationPart = result.data
          parts.push(part)
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FORMAT_PARSE_FAILED] data-citation', result.error)
        }
        break
      }
      case 'data-chart': {
        const result = ChartPartSchema.safeParse({
          type: 'data-chart',
          ...((frame.data as Record<string, unknown>) ?? {}),
        })
        if (result.success) {
          const part: ChartPart = result.data
          parts.push(part)
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FORMAT_PARSE_FAILED] data-chart', result.error)
        }
        break
      }
      case 'data-pending-share': {
        // Without this case the frame fell through the default branch and the
        // confirm card only appeared after a history reload — i.e. never during
        // the turn that proposed the share (greptile on #27487).
        const result = PendingSharePartSchema.safeParse({
          type: 'data-pending-share',
          ...((frame.data as Record<string, unknown>) ?? {}),
        })
        if (result.success) {
          const part = result.data
          // Last proposal wins, same as workflow-progress: one card per turn.
          const existingIndex = parts.findIndex((p) => p.type === 'data-pending-share')
          if (existingIndex >= 0) {
            parts[existingIndex] = part
          } else {
            parts.push(part)
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FORMAT_PARSE_FAILED] data-pending-share', result.error)
        }
        break
      }
      case 'data-workflow-progress': {
        const result = WorkflowProgressPartSchema.safeParse({
          type: 'data-workflow-progress',
          ...((frame.data as Record<string, unknown>) ?? {}),
        })
        if (result.success) {
          const part: WorkflowProgressPart = result.data
          const existingIndex = parts.findIndex((p) => p.type === 'data-workflow-progress')
          if (existingIndex >= 0) {
            parts[existingIndex] = part
          } else {
            parts.push(part)
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[FORMAT_PARSE_FAILED] data-workflow-progress', result.error)
        }
        break
      }
      // start, start-step, finish-step, finish, error → no parts.
      default:
        break
    }
  }
  return parts
}

/** Parse a single SSE chunk into the frames it contains. The payload
 *  may carry multiple `data: ...\n\n` records (proxy buffering); we
 *  split on the SSE record terminator and parse each independently. */
export function parseChunk(buffer: string): {
  frames: IncomingFrame[]
  done: boolean
  remainder: string
} {
  const frames: IncomingFrame[] = []
  let done = false
  let cursor = 0
  while (cursor < buffer.length) {
    const recordEnd = buffer.indexOf('\n\n', cursor)
    if (recordEnd === -1) break
    const record = buffer.slice(cursor, recordEnd).trim()
    cursor = recordEnd + 2
    if (!record.startsWith('data:')) continue
    const payload = record.slice(5).trim()
    if (payload === '[DONE]') {
      done = true
      continue
    }
    try {
      frames.push(JSON.parse(payload) as IncomingFrame)
    } catch (err) {
      // Stale or partial payload — skip. The reduce step is robust to
      // dropped frames (open text/tool runs simply never close, and
      // close() flushes anything still open).
      // eslint-disable-next-line no-console
      console.warn('[FORMAT_PARSE_FAILED] could not parse SSE frame', { payload, err })
    }
  }
  return { frames, done, remainder: buffer.slice(cursor) }
}

export interface UseChatV3Options {
  /** Endpoint URL; defaults to the chatV2 messages endpoint so callers
   *  do not need to know about the v1↔v2 routing layer. */
  endpoint?: string
  /**
   * FS-662 — the retrieval CORPUS for the turn: `false` (default) keeps answers to the
   * board the user is asking from; `true` is the "Use Entire Data Catalog" toggle.
   *
   * Sent on EVERY request, never conditionally. The backend distinguishes three states —
   * `true`, `false`, and ABSENT — and absent is not a synonym for false: it means "no client
   * stated a scope", which is what a dropped toggle looks like in the logs. Omitting the field
   * is precisely the bug FS-662 fixed at the gateway, and re-creating it in a client would
   * make the toggle silently inert again.
   */
  entireDataCatalog?: boolean
}

export interface UseChatV3Helpers {
  message: ChatV3AssistantMessage | null
  send: (conversationId: string, content: string) => Promise<void>
  cancel: () => void
}

/**
 * Hook entry point. The component owns the conversation id — this hook
 * just produces a single assistant message at a time. Callers building
 * a full chat list (e.g. `ChatV2.tsx`) thread this hook's output into
 * their existing message store.
 */
export function useChatV3(options: UseChatV3Options = {}): UseChatV3Helpers {
  const [message, setMessage] = useState<ChatV3AssistantMessage | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  const send = useCallback(
    async (conversationId: string, content: string): Promise<void> => {
      cancel()
      const controller = new AbortController()
      abortRef.current = controller

      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const startedAt = new Date().toISOString()
      setMessage({ id, parts: [], streaming: true, startedAt })

      const url = options.endpoint ?? CHAT_V2_ENDPOINTS.messages(conversationId)
      const token = await getAuthToken()
      const response = await fetch(url, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          [VERCEL_AI_STREAM_HEADER]: VERCEL_AI_STREAM_VERSION,
          // PR 16 — request the Intuigence parts protocol; backend
          // mirrors the value back. We verify the response header
          // below before consuming any frames.
          [INTUIGENCE_PARTS_HEADER]: INTUIGENCE_PARTS_VERSION,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Always states the scope — see `entireDataCatalog` on the options type. An absent
        // field is a distinct backend state ("nobody said"), not a shorthand for false.
        body: JSON.stringify({ content, entire_data_catalog: options.entireDataCatalog ?? false }),
      })

      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => 'request failed')
        setMessage((m) =>
          m
            ? { ...m, streaming: false, error: text.slice(0, 200), completedAt: new Date().toISOString() }
            : m,
        )
        return
      }

      // PR 16 — fail closed when the backend's parts-protocol version
      // disagrees with ours. The header is part of the design's
      // "Vercel SDK version churn" mitigation: we negotiate against
      // OUR vocabulary, not the SDK's. Missing header is permissive
      // (legacy backends pre-PR-16); a present-but-mismatched value
      // is a hard error so we never parse v1 frames as v2.
      const partsVersion = response.headers.get(INTUIGENCE_PARTS_HEADER)
      if (partsVersion !== null && partsVersion !== INTUIGENCE_PARTS_VERSION) {
        setMessage((m) =>
          m
            ? {
                ...m,
                streaming: false,
                error: `parts protocol mismatch: server=${partsVersion} client=${INTUIGENCE_PARTS_VERSION}`,
                completedAt: new Date().toISOString(),
              }
            : m,
        )
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      const allFrames: IncomingFrame[] = []
      let buffer = ''
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const { frames, done: sentinel, remainder } = parseChunk(buffer)
          buffer = remainder
          if (frames.length > 0) {
            allFrames.push(...frames)
            const errFrame = frames.find((f) => f.type === 'error') as
              | { type: 'error'; errorText: string }
              | undefined
            const parts = reduceFramesToParts(allFrames)
            setMessage((m) =>
              m
                ? {
                    ...m,
                    parts,
                    error: errFrame?.errorText,
                  }
                : m,
            )
          }
          if (sentinel) break
        }
      } finally {
        const finalParts = reduceFramesToParts(allFrames)
        setMessage((m) =>
          m
            ? {
                ...m,
                parts: finalParts,
                streaming: false,
                completedAt: new Date().toISOString(),
              }
            : m,
        )
        abortRef.current = null
      }
    },
    [cancel, options.endpoint, options.entireDataCatalog],
  )

  return { message, send, cancel }
}
